import { isApolloError } from '@apollo/client'
import * as React from 'react'
import { VError } from 'verror'

import { captureWithSentry } from '~logic/sentry/captureWithSentry'
import { flushSentry } from '~logic/sentry/flushSentry'

import { ClassErrorBoundary } from './ClassErrorBoundary'
import { useErrorHandlerCtx } from './ErrorHandlerProvider'
import { isChunkError, isNetworkUnreachableError } from './helpers/errorTypeHelpers'

type TProps = RequiredChildren

/**
 * React-ish error boundary to catch error that are not handled any other way in the app
 * doesn’t catch errors in event handlers or async code
 * https://reactjs.org/docs/error-boundaries.html
 */
export const ErrorBoundary: React.FC<TProps> = ({ children }) => {
  const { setError } = useErrorHandlerCtx()

  const onUnhandledError = React.useCallback(
    (error: Error) => {
      if (isChunkError(error)) {
        captureWithSentry({ error, severityLevel: `debug` })
        flushSentry().then(() => window.location.reload())
        return
      }

      if (isApolloError(error) && isNetworkUnreachableError(error)) {
        setError({ type: 'serviceUnreachable', payload: error })
        return
      }

      const sentryEventId = captureWithSentry({ error: new VError(error, 'Unhandled exception') })
      setError({ type: 'otherError', payload: error, sentryEventId })
    },
    [setError],
  )

  const isProd = process.env.NODE_ENV === 'production'

  if (isProd) {
    return <ClassErrorBoundary onError={onUnhandledError}>{children}</ClassErrorBoundary>
  }

  // in development mode: process.env.NODE_ENV === 'development' (i.e appStage === 'local')
  return <>{children}</>
}
